/**
 * Legacy chat wire format — the `lk-chat-topic` data channel that predates
 * LiveKit text streams. Mirrors `setupChat` in @livekit/components-core
 * 0.12.2 (agents-cdn's pinned `useChat` internals), byte-identical to the web
 * adapter's copy in `web/src/transport/legacy-chat.ts`:
 *
 *  - every send goes out twice — once as a text stream on `lk.chat`, once as
 *    a reliable data packet on `lk-chat-topic` for old servers;
 *  - `ignoreLegacy` is the dedup: when the server supports data streams the
 *    legacy copy is stamped `ignoreLegacy: true` and receivers on the legacy
 *    channel drop it, so nobody sees the message twice.
 *
 * The wire shape is duplicated rather than shared because `@ringg/core` may
 * never import a LiveKit SDK (AGENTS.md rule 2) — the same reason the Dart
 * port carries its own copy. Only the codec differs from web: RN has no
 * ambient TextEncoder, so it goes through `lib/utf8`.
 */

import { compareVersions, type Room } from "livekit-client";

import { decodeUtf8, encodeUtf8 } from "../lib/utf8";

/** Text-stream chat topic (`DataTopic.CHAT` in components-core). */
export const CHAT_TOPIC = "lk.chat";

/** Legacy data-channel chat topic (`LegacyDataTopic.CHAT`). */
export const LEGACY_CHAT_TOPIC = "lk-chat-topic";

export interface LegacyChatMessage {
  id: string;
  timestamp: number;
  message: string;
  /** True when the sender also sent the message as a text stream. */
  ignoreLegacy?: boolean;
}

/**
 * `serverSupportsDataStreams` from components-core: LiveKit Cloud
 * (`edition === 1`) or self-hosted newer than 1.8.2 delivers text streams,
 * so the legacy copy can be flagged for receivers to ignore.
 */
export const serverSupportsDataStreams = (room: Room): boolean => room.serverInfo?.edition === 1 || (!!room.serverInfo?.version && compareVersions(room.serverInfo.version, "1.8.2") > 0);

export const encodeLegacyChatMessage = (message: LegacyChatMessage): Uint8Array<ArrayBuffer> => encodeUtf8(JSON.stringify(message));

/** Returns `undefined` for payloads that aren't legacy chat messages. */
export const decodeLegacyChatMessage = (payload: Uint8Array): LegacyChatMessage | undefined => {
  try {
    // Wire boundary: the payload is attacker-influenced JSON, so every field
    // is re-checked below before it becomes a LegacyChatMessage.
    const parsed: unknown = JSON.parse(decodeUtf8(payload));
    if (typeof parsed !== "object" || parsed === null) return undefined;
    const candidate = parsed as Partial<LegacyChatMessage>;
    if (typeof candidate.message !== "string") return undefined;
    return {
      id: typeof candidate.id === "string" ? candidate.id : "",
      timestamp: typeof candidate.timestamp === "number" ? candidate.timestamp : Date.now(),
      message: candidate.message,
      ignoreLegacy: candidate.ignoreLegacy === true,
    };
  } catch {
    return undefined;
  }
};
